<script setup lang="ts">
import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { Map, Plus, Trash2, RefreshCw, Route, Smile } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'save': [code: string]
}>()

const showCode = ref(true)
const copied = ref(false)
const editableCode = ref('')

// Interfaces
interface JourneyTask {
  name: string
  score: number
  actors: string
}

interface JourneySection {
  name: string
  tasks: JourneyTask[]
}

// Estado visual
const journeyTitle = ref('Experiencia de Compra')
const sections = ref<JourneySection[]>([
  {
    name: 'Descubrimiento',
    tasks: [
      { name: 'Buscar producto', score: 5, actors: 'Cliente' },
      { name: 'Navegar opciones', score: 3, actors: 'Cliente' }
    ]
  },
  {
    name: 'Compra',
    tasks: [
      { name: 'Añadir al carrito', score: 6, actors: 'Cliente, Sistema' },
      { name: 'Pago fallido', score: 1, actors: 'Cliente, Banco' }
    ]
  }
])

// Acciones
const addSection = () => {
  sections.value.push({
    name: `Nueva Sección ${sections.value.length + 1}`,
    tasks: []
  })
}

const removeSection = (secIdx: number) => {
  sections.value.splice(secIdx, 1)
}

const addTask = (secIdx: number) => {
  sections.value[secIdx].tasks.push({
    name: 'Nueva Tarea',
    score: 4,
    actors: 'Usuario'
  })
}

const removeTask = (secIdx: number, taskIdx: number) => {
  sections.value[secIdx].tasks.splice(taskIdx, 1)
}

// Generar código desde el estado visual
const generateMermaid = () => {
  let code = `journey\n`
  if (journeyTitle.value.trim()) {
    code += `    title ${journeyTitle.value.trim()}\n`
  }
  
  sections.value.forEach(section => {
    if (section.name.trim()) {
      code += `    section ${section.name.trim()}\n`
    }
    
    section.tasks.forEach(task => {
      const safeName = task.name.trim().replace(/:/g, '-') || 'Tarea'
      const safeActors = task.actors.trim().replace(/:/g, '-')
      
      let taskLine = `      ${safeName}: ${task.score}`
      if (safeActors) {
        taskLine += `: ${safeActors}`
      }
      code += `${taskLine}\n`
    })
  })
  
  editableCode.value = code
}

// Sincronizar desde código a estado visual
const syncToVisual = () => {
  const code = editableCode.value
  const lines = code.split('\n')
  
  const newSections: JourneySection[] = []
  let currentSection: JourneySection = { name: '', tasks: [] }
  let newTitle = ''
  
  for (const line of lines) {
    const tLine = line.trim()
    if (tLine.startsWith('journey')) {
      continue
    } else if (tLine.startsWith('title ')) {
      newTitle = tLine.replace('title ', '').trim()
    } else if (tLine.startsWith('section ')) {
      if (currentSection.name || currentSection.tasks.length > 0) {
        newSections.push(currentSection)
      }
      currentSection = { name: tLine.replace('section ', '').trim(), tasks: [] }
    } else if (tLine.includes(':')) {
      const parts = tLine.split(':').map(s => s.trim())
      if (parts.length >= 2) {
        currentSection.tasks.push({
          name: parts[0],
          score: parseInt(parts[1]) || 3,
          actors: parts.length > 2 ? parts[2] : ''
        })
      }
    }
  }
  
  if (currentSection.name || currentSection.tasks.length > 0) {
    newSections.push(currentSection)
  }
  
  journeyTitle.value = newTitle
  if (newSections.length > 0) {
    sections.value = newSections
  }
}

// Watchers
watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (newVal && !editableCode.value) {
    generateMermaid()
  }
}, { immediate: true })

watch([journeyTitle, sections], () => {
  generateMermaid()
}, { deep: true })

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const saveAndClose = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}
</script>

<template>
  <BaseVisualEditor
    :open="open"
    @update:open="$emit('update:open', $event)"
    title="User Journey (Mapa de Viaje)"
    description="Documenta la experiencia y el estado de ánimo de un usuario a lo largo de un proceso."
    :showCode="showCode"
    @update:showCode="showCode = $event"
    :copied="copied"
    @copy="copyToClipboard"
    @save="saveAndClose"
  >
    <template #icon>
      <Map class="w-5 h-5 text-primary" />
    </template>

    <template #toolbar>
      <Button size="sm" @click="addSection"><Plus class="w-4 h-4 mr-2" /> Añadir Etapa</Button>
    </template>

    <template #canvas>
      <div class="h-full w-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-6">
        
        <!-- Título -->
        <div class="bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-2">
          <label class="text-sm font-semibold">Título del Viaje (Journey)</label>
          <input 
            v-model="journeyTitle" 
            class="border rounded-md px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary font-medium"
            placeholder="Ej. Experiencia de Onboarding"
          />
        </div>

        <!-- Etapas (Secciones) -->
        <div class="flex flex-col gap-6">
          <div 
            v-for="(section, secIdx) in sections" 
            :key="secIdx"
            class="bg-card border rounded-lg shadow-sm flex flex-col overflow-hidden"
          >
            <!-- Cabecera de la Etapa -->
            <div class="bg-primary/10 border-b border-primary/20 p-3 flex items-center justify-between gap-4">
              <div class="flex-1 flex items-center gap-2">
                <Route class="w-4 h-4 text-primary" />
                <input 
                  v-model="section.name" 
                  class="flex-1 bg-transparent text-sm font-bold focus:outline-none focus:border-b border-primary/50 text-foreground"
                  placeholder="Nombre de la Etapa (ej. Descubrimiento)"
                />
              </div>
              <button 
                @click="removeSection(secIdx)" 
                class="text-muted-foreground hover:text-destructive transition-colors p-1 bg-background/50 rounded"
                title="Eliminar Etapa completa"
              >
                <Trash2 class="w-4 h-4" />
              </button>
            </div>

            <!-- Tareas -->
            <div class="p-4 flex flex-col gap-3">
              <div v-if="section.tasks.length === 0" class="text-center text-muted-foreground py-4 text-xs border border-dashed rounded">
                Aún no hay tareas en esta etapa.
              </div>

              <!-- Lista de Tareas -->
              <div 
                v-for="(task, taskIdx) in section.tasks" 
                :key="taskIdx"
                class="flex flex-col lg:flex-row items-start lg:items-center gap-3 bg-muted/40 p-3 rounded-md border"
              >
                <!-- Nombre de la tarea -->
                <div class="flex-1 w-full flex flex-col gap-1">
                  <label class="text-[10px] uppercase font-bold text-muted-foreground ml-1">Acción / Tarea</label>
                  <input 
                    v-model="task.name" 
                    class="bg-background border rounded px-2 py-1 text-sm focus:outline-none focus:border-primary font-medium w-full"
                    placeholder="Ej. Crear cuenta"
                  />
                </div>

                <!-- Puntuación (Satisfacción) -->
                <div class="flex flex-col gap-1 shrink-0 w-full lg:w-32">
                  <label class="text-[10px] uppercase font-bold text-muted-foreground ml-1 flex items-center gap-1">
                    <Smile class="w-3 h-3"/> Satisfacción (1-7)
                  </label>
                  <select 
                    v-model="task.score" 
                    class="bg-background border rounded px-2 py-1 text-sm focus:outline-none focus:border-primary w-full"
                  >
                    <option :value="1">1 - Muy Frustrante</option>
                    <option :value="2">2 - Frustrante</option>
                    <option :value="3">3 - Descontento</option>
                    <option :value="4">4 - Neutral</option>
                    <option :value="5">5 - Contento</option>
                    <option :value="6">6 - Muy Contento</option>
                    <option :value="7">7 - Excelente</option>
                  </select>
                </div>

                <!-- Actores -->
                <div class="flex-1 w-full flex flex-col gap-1">
                  <label class="text-[10px] uppercase font-bold text-muted-foreground ml-1">Actores (Separados por coma)</label>
                  <input 
                    v-model="task.actors" 
                    class="bg-background border rounded px-2 py-1 text-sm focus:outline-none focus:border-primary w-full"
                    placeholder="Ej. Usuario, Sistema"
                  />
                </div>

                <!-- Botón borrar tarea -->
                <button 
                  @click="removeTask(secIdx, taskIdx)" 
                  class="mt-5 lg:mt-4 text-muted-foreground hover:bg-destructive hover:text-white transition-colors p-2 rounded-full"
                  title="Eliminar tarea"
                >
                  <Trash2 class="w-4 h-4" />
                </button>
              </div>

              <!-- Añadir tarea -->
              <Button variant="outline" size="sm" class="mt-2 self-start border-dashed" @click="addTask(secIdx)">
                <Plus class="w-4 h-4 mr-2" /> Añadir Tarea a Etapa
              </Button>
            </div>
          </div>
        </div>

      </div>
    </template>

    <template #code-panel>
      <div class="text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0">
        Mermaid (Sincronizado)
        <Button size="sm" variant="default" class="h-7 text-xs" @click="syncToVisual">
          <RefreshCw class="w-3 h-3 mr-2" />
          Sincronizar a Visual
        </Button>
      </div>
      <MermaidCodeEditor v-model="editableCode" class="flex-1 min-h-0" />
    </template>

    <template #live-preview>
      <MermaidLivePreview :code="editableCode" />
    </template>
  </BaseVisualEditor>
</template>
